import React, { Component } from 'react'
import { connect } from 'react-redux'
import { DateRangePicker } from 'react-date-range'
import FormControl from '@material-ui/core/FormControl'
import Input from '@material-ui/core/Input'
import Popper from '@material-ui/core/Popper'
import Grow from '@material-ui/core/Grow'
import Paper from '@material-ui/core/Paper'
import T from 'prop-types'
import Button from '@material-ui/core/Button'
import { FormattedMessage, injectIntl } from 'react-intl'
import {
  subDays,
  subWeeks,
  subMonths,
  isSameDay,
  addMonths,
  isPast,
  addDays,
  isToday,
  differenceInDays,
} from 'date-fns'
import datePickerLocaleEs from 'date-fns/locale/es'
import datePickerLocaleEn from 'date-fns/locale/en-US'
import messages from './translate'
import 'react-date-range/dist/styles.css'
import 'styles/app/components/date-range.scss'

class DateRangeInput extends Component {
  static defaultProps = {
    direction: 'horizontal',
    startDate: subDays(new Date(), 2),
    endDate: new Date(),
  }

  constructor(props) {
    super(props)
    this.state = {
      range: [
        {
          key: 'selection',
          startDate: subDays(new Date(), 2),
          endDate: new Date(),
          label: this.props.intl.formatMessage(messages.days_3),
        },
      ],
      rangePopup: {
        isOpen: false,
        anchor: null,
      },
      width: window.innerWidth,
      monthCount: 2,
      showMonthAndYear: false,
      direction: this.props.direction,
    }
    this.dateRangeBox = React.createRef()
    this.dateRangeField = React.createRef()
  }

  componentDidMount() {
    this.onResize()
    window.addEventListener('resize', this.onResize)
    document.addEventListener('click', e => this.onClickRangeOutside(e), false)
    const {
      range: [{ startDate, endDate }],
    } = this.state

    this.props.onChange({
      startDate,
      endDate,
    })
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('click', this.onClickRangeOutside, false)

    this.dateRangeBox = null
    this.dateRangeField = null
  }

  onClickRangeOutside(event) {
    const { target } = event

    if (this.dateRangeField && this.dateRangeBox) {
      const [dateRangeField, dateRangeBox] = [
        this.dateRangeField.current,
        this.dateRangeBox.current,
      ]

      if (
        dateRangeBox &&
        !dateRangeField.contains(target) &&
        !dateRangeBox.contains(target)
      ) {
        this.onCloseRangePopup()
      }
    }
  }

  setRangePopupState = (isOpen, anchor) => {
    this.setState({
      rangePopup: {
        isOpen,
        anchor,
      },
    })
  }

  onResize = () => {
    const { width } = this.state

    if (width <= 1200) {
      this.setState({ monthCount: 2 })
    }

    if (width <= 768) {
      this.setState({
        showMonthAndYear: true,
        direction: 'vertical',
      })
    }

    if (width <= 480) {
      this.setState({ monthCount: 1 })
    }

    this.setState({
      width: window.innerWidth,
    })
  }

  onFocus = e => {
    this.setRangePopupState(true, e.currentTarget)
  }

  onCloseRangePopup = () => {
    const [range] = this.state.range
    const { startDate, endDate } = range
    const rangeDiff = Math.abs(differenceInDays(startDate, endDate))
    const today = isToday(startDate) && isToday(endDate)

    if (today) {
      range.startDate = this.props.startDate
      range.endDate = this.props.endDate
    }

    if (rangeDiff < 2 && !today) {
      range.startDate = subDays(startDate, 1)
      range.endDate = addDays(startDate, 1)
    }

    this.props.onChange(range)
    this.setRangePopupState(false, null)
  }

  staticRangeLabel = () => {
    const {
      intl: { formatMessage },
    } = this.props

    return [
      {
        label: formatMessage(messages.days_3),
        range: () => ({
          startDate: subDays(new Date(), 2),
          endDate: new Date(),
          label: formatMessage(messages.days_3),
        }),
        isSelected(range) {
          const definedRange = this.range()

          return (
            isSameDay(range.startDate, definedRange.startDate) &&
            isSameDay(range.endDate, definedRange.endDate)
          )
        },
      },
      {
        label: formatMessage(messages.week_1),
        range: () => ({
          startDate: subWeeks(new Date(), 1),
          endDate: new Date(),
          label: formatMessage(messages.week_1),
        }),
        isSelected(range) {
          const definedRange = this.range()

          return (
            isSameDay(range.startDate, definedRange.startDate) &&
            isSameDay(range.endDate, definedRange.endDate)
          )
        },
      },
      {
        label: formatMessage(messages.weeks_2),
        range: () => ({
          startDate: subWeeks(new Date(), 2),
          endDate: new Date(),
          label: formatMessage(messages.weeks_2),
        }),
        isSelected(range) {
          const definedRange = this.range()

          return (
            isSameDay(range.startDate, definedRange.startDate) &&
            isSameDay(range.endDate, definedRange.endDate)
          )
        },
      },
      {
        label: formatMessage(messages.month_1),
        range: () => ({
          startDate: subMonths(new Date(), 1),
          endDate: new Date(),
          label: formatMessage(messages.month_1),
        }),
        isSelected(range) {
          const definedRange = this.range()

          return (
            isSameDay(range.startDate, definedRange.startDate) &&
            isSameDay(range.endDate, definedRange.endDate)
          )
        },
      },
    ]
  }

  onChangeRange = data => {
    const { selection } = data
    const { label } = selection
    const {
      intl: { formatMessage },
    } = this.props
    const [range] = this.state.range
    const labelsList = this.staticRangeLabel().map(i => i.label)

    if (!(label !== range.label && labelsList.includes(label))) {
      selection.label = formatMessage(messages.customDate)
    }

    this.setState({ range: [selection] })
  }

  render() {
    const {
      rangePopup: { isOpen, anchor },
      monthCount,
      direction,
      showMonthAndYear,
      range,
    } = this.state
    const { locale } = this.props
    const [{ startDate, endDate, label }] = range
    const minDate = subMonths(endDate, 1)
    const maxDate = isPast(addMonths(startDate, 1))
      ? addMonths(startDate, 1)
      : new Date()
    const locales = {
      es: datePickerLocaleEs,
      en: datePickerLocaleEn,
    }

    return (
      <FormControl
        component="div"
        className="db-date-range-picker"
        ref={this.dateRangeField}
        disabled={this.props.disabled}
        onFocus={this.onFocus}>
        <Input readOnly className="date-range-field" value={label} />

        <Popper
          open={isOpen}
          anchorEl={anchor}
          placement="bottom-start"
          transition
          className="date-range-box">
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout={350}>
              <Paper className="date-range" ref={this.dateRangeBox}>
                <DateRangePicker
                  locale={locales[locale]}
                  onChange={this.onChangeRange}
                  months={monthCount}
                  weekStartsOn={1}
                  monthDisplayFormat="MMMM"
                  minDate={minDate}
                  maxDate={maxDate}
                  direction={direction}
                  ranges={range}
                  inputRanges={[]}
                  staticRanges={this.staticRangeLabel()}
                  showMonthAndYearPickers={showMonthAndYear}
                  showSelectionPreview={false}
                  showDateDisplay
                  moveRangeOnFirstSelection={false}
                />
                <Button
                  href={null}
                  variant="contained"
                  color="primary"
                  size="small"
                  className="date-range-button"
                  onClick={this.onCloseRangePopup}>
                  <FormattedMessage {...messages.setupDate} />
                </Button>
              </Paper>
            </Grow>
          )}
        </Popper>
      </FormControl>
    )
  }
}

DateRangeInput.propTypes = {
  onChange: T.func.isRequired,
  startDate: T.object,
  endDate: T.object,
  ranges: T.array,
  direction: T.oneOf(['horizontal', 'vertical']),
}

export default connect(
  state => ({
    locale: state.getIn(['profile', 'profile', 'locale']),
  }),
  null,
)(injectIntl(DateRangeInput))
